/**
 * Resolve battle HUD title strings: game state carries English (en-us) display
 * from the server; the profile titles catalog maps that canonical string to the
 * user's current UI language.
 */

/**
 * Match {@link modules/translations} normalizeLanguageCode so /profile/titles?lang=…
 * hits titles_translations rows (en-us, de-de, es-es).
 * @param {string | undefined | null} lng
 * @returns {'en-us'|'de-de'|'es-es'|string}
 */
export function normalizeTitlesApiLang(lng) {
  if (!lng) return 'en-us';
  const n = String(lng).toLowerCase().replace(/_/g, '-');
  if (n === 'en' || n.startsWith('en-')) return 'en-us';
  if (n === 'de' || n.startsWith('de-')) return 'de-de';
  if (n === 'es' || n.startsWith('es-')) return 'es-es';
  return n;
}

/**
 * @param {Array<{ id?: number, name: string, nameAsInjectedForBattle?: string, nameEn?: string }>} titles
 * @returns {Map<string, string>}
 */
export function buildBattleTitleLookupFromProfileTitles(titles) {
  if (!Array.isArray(titles)) {
    throw new Error('titles must be an array');
  }
  const m = new Map();
  for (const row of titles) {
    if (typeof row.name !== 'string' || row.name === '') {
      throw new Error(`Title id ${row.id}: localized name is required`);
    }
    const keys = new Set();
    if (row.nameAsInjectedForBattle != null && row.nameAsInjectedForBattle !== '') {
      keys.add(String(row.nameAsInjectedForBattle));
    }
    if (row.nameEn != null && row.nameEn !== '') {
      keys.add(String(row.nameEn));
    }
    if (keys.size === 0) {
      throw new Error(
        `Title id ${row.id}: need nameAsInjectedForBattle and/or nameEn for battle title lookup`
      );
    }
    for (const k of keys) {
      if (m.has(k) && m.get(k) !== row.name) {
        throw new Error(`Battle title lookup key conflict: ${JSON.stringify(k)}`);
      }
      m.set(k, row.name);
    }
  }
  return m;
}

/**
 * @param {string|null|undefined} rawFromGameState
 * @param {Map<string, string>|null} lookupMap from {@link buildBattleTitleLookupFromProfileTitles}; null while loading or on fetch error
 * @param {import('i18next').TFunction} t
 * @returns {string}
 */
export function getLocalizedBattleTitle(rawFromGameState, lookupMap, t) {
  const raw =
    rawFromGameState == null || rawFromGameState === '' ? 'The undecided' : String(rawFromGameState);
  if (raw === 'The undecided') {
    return t('battleUI.titleUndecided');
  }
  if (lookupMap == null) {
    return stripLeadingTitleGlyphs(raw);
  }
  const localized = lookupMap.get(raw);
  if (localized === undefined) {
    throw new Error(`Battle title string not found in profile/titles lookup: ${JSON.stringify(raw)}`);
  }
  return stripLeadingTitleGlyphs(localized);
}

/**
 * Some title rows include decorative leading symbols (e.g. bird/emblem glyphs).
 * The HUD title frame should display plain text only.
 * @param {string} title
 * @returns {string}
 */
function stripLeadingTitleGlyphs(title) {
  if (typeof title !== 'string') {
    throw new Error('stripLeadingTitleGlyphs: title must be a string');
  }
  return title.replace(/^[^\p{L}\p{N}]+/u, '');
}

/**
 * Load one title's display name in the given UI language (titles_translations via /profile/titles).
 * @param {string} apiUrl - e.g. API_URL from config
 * @param {string} token - Bearer JWT
 * @param {number|string} titleId
 * @param {string|undefined|null} i18nLanguage
 * @returns {Promise<string>}
 */
export async function fetchLocalizedTitleNameById(apiUrl, token, titleId, i18nLanguage) {
  if (titleId == null || titleId === '') {
    throw new Error('fetchLocalizedTitleNameById: titleId is required');
  }
  if (!token || typeof token !== 'string') {
    throw new Error('fetchLocalizedTitleNameById: token is required');
  }
  const lang = normalizeTitlesApiLang(i18nLanguage);
  const res = await fetch(`${apiUrl}/profile/titles?lang=${encodeURIComponent(lang)}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Accept-Language': lang,
    },
  });
  if (!res.ok) {
    throw new Error(`fetchLocalizedTitleNameById: GET /profile/titles failed with status ${res.status}`);
  }
  const data = await res.json();
  const titles = data.titles;
  if (!Array.isArray(titles)) {
    throw new Error('fetchLocalizedTitleNameById: response missing titles array');
  }
  const idNum = Number(titleId);
  const row = titles.find((t) => Number(t.id) === idNum);
  if (!row || typeof row.name !== 'string' || row.name === '') {
    throw new Error(`fetchLocalizedTitleNameById: title id ${titleId} missing or has empty name in catalog`);
  }
  return row.name;
}
